import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import {
  DEFAULT_SHORTLIST_SORT,
  ShortListCustomField,
  ShortlistCategoriesConfigProps,
  ShortlistSort,
  ShortlistSortOrder,
  ShortlistTableConfigProps,
  ShortlistTableHeadProps,
} from '@modules/shortlist/types'
import { TableRow } from '@mui/material'
import { DRAG_AUTO_SCROLL_PX_AMOUNT, TOP_SCROLL_THRESHOLD } from '@shared/constants'

import CustomColumnHeadCell from './CustomColumnHeadCell'
import ProfileTitleHeadCell from './ProfileTitleHeadCell'
import ProfileTitleTagHeadCell from './ProfileTitleTagHeadCell'
import TableHeadCell from './TableHeadCell'
import { TitleHeading } from './index.styles'

export const ShortlistOverviewTableColumnHeaders = (props: ShortlistTableHeadProps) => {
  const {
    optionalColumnsConfig,
    allProfilesSelected,
    onSelectAllProfiles,
    sort,
    onSortChange,
    tableColumns,
    activeShortlistId,
    activeShortlist,
    onEditCustomColumnButtonClick,
    updateConfig,
  } = props

  const { t } = useTranslation()
  const [fieldWithActiveSort, setFieldWithActiveSort] = useState<string | undefined>(DEFAULT_SHORTLIST_SORT.fieldName)
  const [showEditCustomTagsPopup, setShowEditCustomTagsPopup] = useState(false)

  useEffect(() => {
    setFieldWithActiveSort(sort.fieldName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSortChange = useCallback(
    ({ fieldName, sortOrder }: ShortlistSort) => {
      if (!sortOrder) {
        handleSortChange(DEFAULT_SHORTLIST_SORT)
        return
      }
      setFieldWithActiveSort(fieldName as string)
      onSortChange({ fieldName, sortOrder })
      onSelectAllProfiles(false)
    },
    [onSelectAllProfiles, onSortChange, setFieldWithActiveSort],
  )

  const handleCustomSortChange = useCallback(
    ({ customFieldId, sortOrder }: { customFieldId?: number; sortOrder?: ShortlistSortOrder }) => {
      if (!sortOrder) {
        handleSortChange(DEFAULT_SHORTLIST_SORT)
        return
      }

      setFieldWithActiveSort(undefined)
      onSortChange({ fieldName: 'customFieldValues', sortOrder, customFieldId })
      onSelectAllProfiles(false)
    },
    [onSelectAllProfiles, onSortChange, setFieldWithActiveSort, handleSortChange],
  )

  // @TODO: refactor, use just generic "render" method
  const tableHeaderColumnsComponents: Record<ShortlistTableConfigProps['fieldName'], () => ReactNode> = {
    // eslint-disable-next-line react/no-unstable-nested-components
    profileTitle: () => (
      <ProfileTitleHeadCell
        allProfilesSelected={allProfilesSelected}
        onSelectAllProfiles={onSelectAllProfiles}
        sortOrder={sort?.sortOrder}
        isSortActive={!!sort && fieldWithActiveSort === 'profileTitle'}
      />
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    tagId: () => (
      <ProfileTitleTagHeadCell
        activeShortlistId={activeShortlistId}
        showEditCustomTagsPopup={showEditCustomTagsPopup}
        setShowEditCustomTagsPopup={setShowEditCustomTagsPopup}
        sortOrder={sort?.sortOrder}
        isSortActive={!!sort && fieldWithActiveSort === 'tagId'}
      />
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    profileContact: () => <TitleHeading variant="h3">{t(translationKeys.CUSTOM_LIST_TABLE_HEADER_CONTACT)}</TitleHeading>,
  }

  const customColumnHeaderComponent = (customField?: ShortListCustomField) => {
    return (
      <CustomColumnHeadCell
        key={customField?.id}
        onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
        sortOrder={sort?.sortOrder}
        isSortActive={sort.customFieldId === customField?.id}
        headerTitle={customField?.fieldName}
        customFieldId={customField?.id}
        onSortChange={handleCustomSortChange}
      />
    )
  }

  // simulate auto-scroll to top for browsers that dont support it and make smoother for others
  useEffect(() => {
    const eventHandler = (e: DragEvent) => {
      const top = e.clientY

      if (top < TOP_SCROLL_THRESHOLD && top > 0) {
        window.scrollTo(0, window.scrollY - DRAG_AUTO_SCROLL_PX_AMOUNT)
      }
    }

    window.addEventListener('dragover', eventHandler)

    return () => {
      window.removeEventListener('dragover', eventHandler)
    }
  }, [])

  return (
    <TableRow>
      {tableColumns.map(tableColumn => {
        // @ts-expect-error TODO: column def typing
        const { columnTitle, restCellProps, fieldName, isEnabled, isSortable } = tableColumn.column.columnDef

        const customField = activeShortlist?.customFields?.find(field => field?.id.toString() === fieldName.toString())

        const cellComponent = customField
          ? customColumnHeaderComponent
          : tableHeaderColumnsComponents[fieldName as keyof ShortlistTableConfigProps]
        if (
          !(
            isEnabled ||
            (optionalColumnsConfig && optionalColumnsConfig[fieldName as keyof ShortlistCategoriesConfigProps]?.visible)
          )
        )
          return null

        const isSortActive = fieldWithActiveSort === fieldName

        return (
          <TableHeadCell
            tableColumn={tableColumn}
            key={fieldName}
            fieldName={fieldName}
            restCellProps={restCellProps}
            cellComponent={cellComponent}
            columnTitle={columnTitle}
            isSortable={isSortable}
            isSortActive={isSortActive}
            sortOrder={sort?.sortOrder}
            updateConfig={updateConfig}
            optionalColumnsConfig={optionalColumnsConfig}
            customField={customField}
            onSortChange={handleSortChange}
          />
        )
      })}
    </TableRow>
  )
}
